import { MEDICATION_STATUS_MAP } from '@models/medication';
import type { MedicationStatementRecord } from '@models/medication';

import { sortMedications } from '@utils/sort/records';

import { StatementStatus } from 'generated/types';

export type MedicationReportSection = 'active' | 'past';

export type MedicationReportGroups = {
  active: MedicationStatementRecord[];
  past: MedicationStatementRecord[];
};

/**
 * The API's `status` field is typed as GraphQL `String`, so it returns the
 * raw FHIR wire value (e.g. `"on-hold"`) rather than the generated
 * `StatementStatus` enum's name (`StatementStatus.onHold` = `"onHold"`).
 * Normalize before any comparison/lookup below so hyphenated statuses
 * aren't silently dropped. Returns undefined for anything that isn't one
 * of the 8 statuses in the FHIR MedicationStatement status value set.
 */
const FHIR_STATUS_TO_STATEMENT_STATUS: Record<string, StatementStatus> = {
  active: StatementStatus.active,
  completed: StatementStatus.completed,
  'entered-in-error': StatementStatus.enteredInError,
  intended: StatementStatus.intended,
  stopped: StatementStatus.stopped,
  'on-hold': StatementStatus.onHold,
  unknown: StatementStatus.unknown,
  'not-taken': StatementStatus.notTaken,
};

function normalizeMedicationStatus(
  status: string
): StatementStatus | undefined {
  return FHIR_STATUS_TO_STATEMENT_STATUS[status];
}

/**
 * `entered-in-error` statements are erroneous data entry rather than real
 * medication history, so they're excluded entirely from both the shared
 * link and the PDF export — not shown in either section.
 */
export function getMedicationReportSection(
  medication: MedicationStatementRecord
): MedicationReportSection | null {
  switch (normalizeMedicationStatus(medication.status)) {
    case StatementStatus.active:
    case StatementStatus.intended:
      return 'active';
    case StatementStatus.completed:
    case StatementStatus.stopped:
    case StatementStatus.onHold:
    case StatementStatus.notTaken:
    case StatementStatus.unknown:
      return 'past';
    case StatementStatus.enteredInError:
    default:
      return null;
  }
}

/** Human-readable status label, so Past entries with different statuses stay distinguishable. */
export function getMedicationReportStatusLabel(
  medication: MedicationStatementRecord
): string {
  const status = normalizeMedicationStatus(medication.status);
  return status ? MEDICATION_STATUS_MAP[status] : medication.status;
}

/**
 * Latest start date first. Medications with no start date sort after every
 * dated medication (there's nothing to rank them against chronologically),
 * then falls back to name, then id, so ties resolve the same way on every render.
 */
export function sortMedicationsForReport(
  a: MedicationStatementRecord,
  b: MedicationStatementRecord
): number {
  const startA = a.getDateStart();
  const startB = b.getDateStart();

  if (startA && startB) {
    const byDate = startB.getTime() - startA.getTime();
    if (byDate !== 0) {
      return byDate;
    }
  } else if (startA) {
    return -1;
  } else if (startB) {
    return 1;
  }

  const byName = sortMedications(a, b);
  if (byName !== 0) {
    return byName;
  }
  return (a.id ?? '').localeCompare(b.id ?? '');
}

/**
 * Groups and sorts a patient's medications into the two report sections
 * shared by "share full list" and "export PDF" — the single source of truth
 * for that split so the two features can't drift apart.
 */
export function groupMedicationsForReport(
  medications: MedicationStatementRecord[]
): MedicationReportGroups {
  const active: MedicationStatementRecord[] = [];
  const past: MedicationStatementRecord[] = [];

  for (const medication of medications) {
    const section = getMedicationReportSection(medication);
    if (section === 'active') {
      active.push(medication);
    } else if (section === 'past') {
      past.push(medication);
    }
  }

  active.sort(sortMedicationsForReport);
  past.sort(sortMedicationsForReport);

  return { active, past };
}
